// Персист кэша TanStack Query на диск (фаза 3 — оффлайн кабинета).
//
// Кабинет (каталог, персонал, точки) жил ТОЛЬКО в памяти QueryClient: `gcTime`
// 30 минут и никакого persist. Приложение, перезапущенное без сети, отрисовывало
// пустой каталог и «нет связи» на каждый список — а по спецификации админ должен
// видеть меню и офлайн.
//
// Кухонные экраны (журнал, мониторинг, меню печати) СЮДА НЕ ПОПАДАЮТ: у них свой
// кэш в Dexie (`labelCache`, T-036) и в Preferences (меню). Дублировать снимок
// не нужно, а лишний вес снимка замедляет гидрацию на старте моноблока.

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { Query } from '@tanstack/react-query';

import { isSupportSession } from '@/shared/auth';

import { offlineDb } from './db';

const SNAPSHOT_KEY = 'query-cache';

/** Домены кабинета, которые переживают перезапуск. Ключ запроса начинается с одного из них. */
const PERSISTED_DOMAINS = new Set(['catalog', 'staff', 'locations']);

/** Хранилище снимка — таблица `kv` Dexie. Один ключ, один блоб. */
const dexieStorage = {
  getItem: async (key: string): Promise<string | null> => {
    const row = await offlineDb.kv.get(key);
    return typeof row?.value === 'string' ? row.value : null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await offlineDb.kv.put({ key, value });
  },
  removeItem: async (key: string): Promise<void> => {
    await offlineDb.kv.delete(key);
  },
};

export const queryPersister = createAsyncStoragePersister({
  storage: dexieStorage,
  key: SNAPSHOT_KEY,
  // Снимок пишется не чаще раза в секунду: правка каталога не должна дёргать диск
  // на каждый символ в поле.
  throttleTime: 1000,
});

/** В снимок идут только успешные запросы кабинетных доменов. */
export function shouldPersistQuery(query: Query): boolean {
  // Саппорт-сессия платформы (T-039): каталог и персонал клиента на диск не уходят.
  if (query.state.status !== 'success' || isSupportSession()) {
    return false;
  }
  const head = query.queryKey[0];
  return typeof head === 'string' && PERSISTED_DOMAINS.has(head);
}

/**
 * Снимок протухает через семь дней — то же окно, что у офлайн-операций (T-037).
 * Каталог недельной давности лучше пустого экрана, но месячному верить нельзя.
 */
export const QUERY_SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Стереть снимок — зовётся при выходе из аккаунта вместе с остальным кэшем. */
export async function clearQuerySnapshot(): Promise<void> {
  await offlineDb.kv.delete(SNAPSHOT_KEY);
}
